import type {User} from "account/User";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import {dateTimeFormat} from "core/format/intlFormats";
import {minorToMajor} from "core/format/minorUnits";
import {planOf, type Mode} from "home/gating";
import type {Locale} from "i18n/localePreference";
import type {BillingPeriod, PaidPlanId} from "pricing/planCatalogue";
import type {CouponGift, RegularPriceQuote} from "offers/ladder/currentLadderOffer";
import {resumeFreeCheckout, type FreeCheckoutPorts} from "offers/resumeFreeCheckout";

/**
 * What a limit-wall email link is worth on the send screen (kitecorp/transferify-api-service#2206,
 * landed on `/?wall=` by kitecorp/transferify-ui#3197).
 *
 * About an hour after a Free sender meets the hosted size wall or the Instant wall and does not pay,
 * the api mails one coupon for the plan that fits what they tried to send. Its link goes to the api,
 * which records the click and redirects to `/?wall=<token>`: the send screen, where the sender can
 * finish the blocked transfer, with the offer as a slide-out card beside the composer. This module
 * owns every rule the screen then applies, and imports no React.
 *
 * <b>The token is a bearer credential and is never persisted</b>, for the ladder's reasons
 * (`offers/ladder/ladderOffer`): it authorizes one account's discount, so it lives in the address bar
 * and in component state only.
 *
 * <b>Every figure is the api's.</b> The api resolves the live code, the first invoice and the renewal in
 * the reader's currency at the moment the screen asks; nothing here recomputes them from the baked
 * catalogue, and nothing here judges the currency, because the checkout's `/plan` read picks the price
 * by the same edge-country-first order the resolve answered on. The size the card names and the mode
 * the composer preselects are the wall's own facts, stored by the api when the wall was met.
 *
 * <b>The surface is SERVER-witnessed</b> (`UpsellMoment.LIMIT_WALL_EMAIL`): the api records clicked,
 * checkout_opened and paid itself, so the browser reports no funnel step for it
 * (see `clientReportsCheckoutStep` in `offers/ladder/ladderOffer`).
 */

/** The query parameter the api's redirect carries, mirroring `LimitWallOfferService.LINK_PARAMETER`. */
export const LIMIT_WALL_PARAMETER = "wall";

/** Every minted token starts with this, mirroring `LimitWallOffers.TOKEN_PREFIX`. */
export const LIMIT_WALL_TOKEN_PREFIX = "wall.";

/** The widest token the api will look up, mirroring `LimitWallOffers.MAX_TOKEN_LENGTH`. */
export const LIMIT_WALL_TOKEN_MAX_LENGTH = 64;

/** `LimitWallOfferResponse`: what the link offers right now. Amounts are minor units in `currency`. */
export interface LimitWallOffer {
	targetPlan: "FREE" | "BASIC" | "PLUS" | "PREMIUM";
	/** The link's 72 hours are up; the prices and the coupon are then absent. */
	expired: boolean;
	/**
	 * The send mode the wall was met on, which the composer preselects. Optional only because an api from
	 * before kitecorp/transferify-ui#3197 answers without it; the current api always states it.
	 */
	wall?: "HOSTED" | "INSTANT";
	/** The size the sender tried to send when the wall was met, in bytes. Optional for the same reason. */
	attemptedBytes?: number;
	/** When the link's 72 hours end, as a UTC instant; the checkout dialog names the day. Same reason. */
	expiresAt?: string;
	currency?: string | null;
	percentOff?: number | null;
	amountOff?: number | null;
	firstMonth?: number | null;
	renewal?: number | null;
	/** 0 means checkout is at the full price (owner decision C7). */
	discountsLeftToday: number;
	maxHostedUploadBytes?: number | null;
}

/*
 * Compile-time binding to the generated spec: a renamed or retyped field fails `tsc -b` here, naming the
 * field, rather than the card quoting a figure it misread.
 *
 * `wall`, `attemptedBytes` and `expiresAt` are exempt because this ui ships AHEAD of the api that answers
 * them (kitecorp/transferify-ui#3197, the api half in `LimitWallOfferResponse.java`): the committed DEV
 * snapshot predates that deploy. The three are read defensively below, so an older api still lands the
 * offer. Remove the exemption with the `chore(api): sync deployed DEV schema` commit that carries them.
 */
export type _LimitWallOffer = AssertNoSpecDrift<SpecDrift<LimitWallOffer, Schemas["LimitWallOfferResponse"],
	"wall" | "attemptedBytes" | "expiresAt">>;

/**
 * What asking the api produced. `refused` is the api saying the token names no send (404) or is
 * malformed (400); `unavailable` is our own transport failing, which says nothing about the link.
 */
export type LimitWallResolution =
	| {status: "offer"; offer: LimitWallOffer}
	| {status: "refused"}
	| {status: "unavailable"};

/**
 * The wall's own facts, as the send screen uses them on arrival: the mode to preselect, the size the card
 * names and the day the checkout dialog names. Each is null when the api stated none or stated it
 * unreadably, and the screen then simply says less; the offer itself does not depend on them.
 */
export interface LimitWallArrival {
	mode: Mode | null;
	attemptedBytes: number | null;
	expiresAt: string | null;
}

/**
 * What the send screen shows for the link that was followed.
 *
 * - `none`: the ordinary screen, nothing to say.
 * - `expired`: the one-line expired notice over the ordinary composer.
 * - `coupon`: the offer card, whose button opens the claim dialog with the api's coupon. `withdrawn` is a
 *   coupon the till refused for this reader while the api still calls it live (another account's link,
 *   or a paid plan since the email): the dialog says the offer is gone and never buys at another price
 *   on its own.
 * - `regular`: the same card and dialog at the plan's full price, with the note that today's offers are
 *   used up.
 */
export type LimitWallPresentation =
	| {kind: "none"}
	| {kind: "expired"}
	| {kind: "coupon"; gift: CouponGift; discountsLeft: number; withdrawn: boolean; arrival: LimitWallArrival}
	| {kind: "regular"; quote: RegularPriceQuote; arrival: LimitWallArrival};

/** The token out of a `location.search`-style string, or null when there is none this screen may ask about. */
export function parseLimitWallToken(search: string): string | null {
	const value = new URLSearchParams(search.replace(/^\?/, "")).get(LIMIT_WALL_PARAMETER)?.trim();
	if (value === undefined || !value.startsWith(LIMIT_WALL_TOKEN_PREFIX)
		|| value.length === LIMIT_WALL_TOKEN_PREFIX.length || value.length > LIMIT_WALL_TOKEN_MAX_LENGTH) {
		return null;
	}
	return value;
}

/** The same search with the token removed, for the expired path; every other parameter stays. */
export function withoutLimitWallToken(search: string): string {
	const params = new URLSearchParams(search.replace(/^\?/, ""));
	params.delete(LIMIT_WALL_PARAMETER);
	const rest = params.toString();
	return rest.length === 0 ? "" : `?${rest}`;
}

const PAID_TARGETS: Record<LimitWallOffer["targetPlan"], PaidPlanId | null> = {
	FREE: null, BASIC: "basic", PLUS: "plus", PREMIUM: "premium",
};

/** The api's wall names, mirroring `OverLimitPricing`, onto the composer's own mode ids. */
const WALL_MODES: Record<NonNullable<LimitWallOffer["wall"]>, Mode> = {HOSTED: "upload", INSTANT: "instant"};

const isWhole = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value);

/**
 * What the screen shows, from what the api answered and whether the till has already refused the token.
 *
 * The order is the rule. A refusal or an expired link comes first and never reveals a price. Then
 * `discountsLeftToday`: 0 is the full price (C7) whatever else rides along, because the api's schema says
 * 0 means checkout is at the full price. Only then is a coupon read, and only one whose every figure can
 * be stated honestly; anything else is the ordinary screen rather than a guess.
 *
 * <b>After a refusal the reader is never left with nothing.</b> The api refused the token at the till, so
 * a re-read that cannot state the full price or a still-live coupon is the expired notice: nothing was
 * charged, and the composer under the notice still sends.
 */
export function limitWallPresentation(
	resolution: LimitWallResolution | null, refusedAtTill: boolean,
): LimitWallPresentation {
	const answered = presentationOf(resolution);
	if (!refusedAtTill) return answered;
	if (answered.kind === "coupon") return {...answered, withdrawn: true};
	return answered.kind === "none" ? {kind: "expired"} : answered;
}

function presentationOf(resolution: LimitWallResolution | null): LimitWallPresentation {
	if (resolution === null || resolution.status === "unavailable") return {kind: "none"};
	if (resolution.status === "refused" || resolution.offer.expired) return {kind: "expired"};
	const {targetPlan, currency, percentOff, amountOff, firstMonth, renewal, discountsLeftToday, maxHostedUploadBytes} = resolution.offer;
	const plan = PAID_TARGETS[targetPlan];
	if (plan === null || typeof currency !== "string" || currency.length === 0 || !isWhole(renewal) || renewal <= 0
		|| !isWhole(discountsLeftToday) || discountsLeftToday < 0
		|| (maxHostedUploadBytes != null && (!isWhole(maxHostedUploadBytes) || maxHostedUploadBytes <= 0))) {
		return {kind: "none"};
	}
	const arrival = arrivalOf(resolution.offer);
	const quote: RegularPriceQuote = {
		targetPlan: plan, currency, discount: null, renewal: minorToMajor(renewal, currency),
		maxHostedUploadBytes: maxHostedUploadBytes ?? null, isUpgrade: false,
	};
	if (discountsLeftToday === 0) return {kind: "regular", quote, arrival};
	if (!isWhole(firstMonth) || firstMonth < 0 || firstMonth > renewal) return {kind: "none"};
	const discount = discountOf(percentOff, amountOff, currency);
	if (discount === null) return {kind: "none"};
	return {
		kind: "coupon", discountsLeft: discountsLeftToday, withdrawn: false, arrival,
		gift: {...quote, discount, firstMonth: minorToMajor(firstMonth, currency)},
	};
}

/**
 * The wall's facts, each read on its own: a fact the api stated unreadably costs that fact alone, never
 * the offer. A size has to be a positive whole number of bytes and an end has to parse as a date; a wall
 * has to be one the composer has a mode for.
 */
function arrivalOf(offer: LimitWallOffer): LimitWallArrival {
	const {wall, attemptedBytes, expiresAt} = offer;
	return {
		mode: wall !== undefined && wall in WALL_MODES ? WALL_MODES[wall] : null,
		attemptedBytes: isWhole(attemptedBytes) && attemptedBytes > 0 ? attemptedBytes : null,
		expiresAt: typeof expiresAt === "string" && !Number.isNaN(Date.parse(expiresAt)) ? expiresAt : null,
	};
}

function discountOf(percentOff: number | null | undefined, amountOff: number | null | undefined,
	currency: string): CouponGift["discount"] | null {
	if (percentOff != null && amountOff == null) {
		return isWhole(percentOff) && percentOff > 0 && percentOff <= 100 ? {kind: "percent", percentOff} : null;
	}
	if (amountOff != null && percentOff == null) {
		return isWhole(amountOff) && amountOff > 0 ? {kind: "amount", amountOff: minorToMajor(amountOff, currency)} : null;
	}
	return null;
}

/** The offer card's title: the size the wall saw, that the selection on screen now fits the plan, or the plan alone. */
export type LimitWallCardTitle = {kind: "size"; bytes: number} | {kind: "covers"} | {kind: "plain"};

/**
 * The offer card beside the composer (kitecorp/transferify-ui#3197): a title, one line and one button.
 * Amounts are major units in `currency`, already converted by the presentation.
 */
export interface LimitWallCardModel {
	plan: PaidPlanId;
	title: LimitWallCardTitle;
	currency: string;
	/** The discounted first invoice, or null at the full price (C7) and for a coupon the till withdrew. */
	firstMonth: number | null;
	renewal: number;
	/** The plan's per-transfer hosted limit, which the card's line states; null where the api quoted none. */
	maxHostedUploadBytes: number | null;
	/** Whether the button sells a discount: it says "Use the discount" only when there is one to use. */
	discounted: boolean;
}

/**
 * The card for the presentation, or null when there is no card: the expired notice and the ordinary
 * screen show none.
 *
 * <b>The title follows the files once they are back</b> (owner 2026-10-03): while the composer is empty
 * it names the size the wall saw, "Send your 18.4 GB with Basic", because that is what the sender came
 * back for. Once a selection is on screen and fits the offered plan it reads that the plan covers these
 * files, whatever their size, so a sender who added fewer files than before is not told the old number.
 * A selection OVER the plan's own limit keeps the wall's size: the plan could not send what is on screen,
 * and a title claiming it covers them would be untrue. Both rules need a size to measure against, so an
 * api that stated no size names the plan alone and never claims to cover files it cannot measure.
 *
 * `selectionBytes` is the selection's total, or null while the composer is empty.
 */
export function limitWallCard(presentation: LimitWallPresentation, selectionBytes: number | null): LimitWallCardModel | null {
	if (presentation.kind !== "coupon" && presentation.kind !== "regular") return null;
	const quote = presentation.kind === "coupon" ? presentation.gift : presentation.quote;
	const discounted = presentation.kind === "coupon" && !presentation.withdrawn;
	return {
		plan: quote.targetPlan,
		title: titleOf(presentation.arrival.attemptedBytes, quote.maxHostedUploadBytes, selectionBytes),
		currency: quote.currency,
		firstMonth: discounted && presentation.kind === "coupon" ? presentation.gift.firstMonth : null,
		renewal: quote.renewal,
		maxHostedUploadBytes: quote.maxHostedUploadBytes,
		discounted,
	};
}

function titleOf(attemptedBytes: number | null, maxHostedUploadBytes: number | null, selectionBytes: number | null): LimitWallCardTitle {
	if (attemptedBytes === null) return {kind: "plain"};
	if (selectionBytes !== null && maxHostedUploadBytes !== null && selectionBytes <= maxHostedUploadBytes) return {kind: "covers"};
	return {kind: "size", bytes: attemptedBytes};
}

/**
 * The card's one line, assembled from sentences the catalog already formatted: the price first, then
 * the plan's per-transfer limit. When a card pitching the same plan was dropped for this offer (owner
 * 2026-10-03, `slideOutCardsBesideOffer`), its reason takes the limit's place and LEADS, because the
 * reader's own usage is the more pressing fact: "41 of 50 downloads used. €3.99 for the first month,
 * then €4.99." Still one line, never a third.
 */
export function limitWallCardLine(lead: string | null, price: string, limit: string | null): string {
	if (lead !== null) return `${lead}. ${price}`;
	return limit === null ? price : `${price} ${limit}`;
}

/**
 * The day the offer ends, as the checkout dialog's ticket names it: a weekday in the reader's language
 * and the reader's OWN time zone, never a clock (owner 2026-10-03). Null when the api stated no end, an
 * unreadable one, or one already past, in which case the dialog's line stays empty rather than naming a
 * day that has gone.
 *
 * `timeZone` is for tests; a caller leaves it out and gets the browser's zone.
 */
export function limitWallOfferEndsOn(expiresAt: string | null, locale: Locale, now: number, timeZone?: string): string | null {
	if (expiresAt === null) return null;
	const at = Date.parse(expiresAt);
	if (Number.isNaN(at) || at <= now) return null;
	return dateTimeFormat(locale, {weekday: "long", timeZone}).format(at);
}

/** What a Subscribe press starts: always the target plan, monthly; the token only while the coupon is live. */
export interface LimitWallCheckout {
	plan: PaidPlanId;
	period: Extract<BillingPeriod, "MONTHLY">;
	terms: {offerToken?: string};
}

/**
 * The checkout a press starts, or null when it must start none.
 *
 * <b>A live coupon carries exactly the link's token</b>, and the api attaches exactly the email's code or
 * refuses. <b>The full price (C7) is an ORDINARY checkout for the same plan, WITHOUT the token</b>: the
 * dialog already quoted the full price, and a token would only be refused. A withdrawn coupon starts
 * nothing, so a refusal is never silently turned into a full-price purchase.
 */
export function limitWallCheckout(presentation: LimitWallPresentation, token: string | null): LimitWallCheckout | null {
	if (presentation.kind === "regular") {
		return {plan: presentation.quote.targetPlan, period: "MONTHLY", terms: {}};
	}
	if (presentation.kind !== "coupon" || presentation.withdrawn || token === null) return null;
	return {plan: presentation.gift.targetPlan, period: "MONTHLY", terms: {offerToken: token}};
}

/**
 * Whether the offer card is in the slide-out stack: a coupon or the full price, for a guest or a Free
 * account, once the session has settled for the first time, and not once this visit has paid. A paid
 * account never sees it; the api would refuse it.
 *
 * <b>The session gates the card</b> ({@link sessionSettledOnce}), so a returning paid subscriber never
 * sees the offer flash while their account is still being read. `paid` covers the moment between the
 * dialog's confirmation and the account re-read that moves the plan: the card leaves with the payment,
 * not a second later.
 *
 * Tucking the card is the stack's own this-visit fact, not this rule's: a reload of the still-live link
 * shows the card again.
 *
 * <b>`welcomeOn` sets the card aside</b> (owner 2026-10-27, kitecorp/transferify#536): it is
 * `pricing/welcomePrice.offersYieldToWelcome`, true while a welcome run is on or while its read has not
 * answered (bounded by `WELCOME_READ_TIMEOUT_MS`), and the welcome price is then the only offer.
 */
export function limitWallCardDue(presentation: LimitWallPresentation, user: User, settled: boolean, paid: boolean,
	welcomeOn: boolean): boolean {
	return (presentation.kind === "coupon" || presentation.kind === "regular") && settled && !paid && !welcomeOn
		&& planOf(user) === "free";
}

/**
 * Whether the claim dialog is on screen: only once the card's button asked for it (`requested`, a
 * this-visit fact the hook holds until the dialog is dismissed), for a reader the card is for. A guest's
 * sign-in from the dialog's own Subscribe keeps it open through the session's re-read, because
 * `requested` does not depend on the session; a paid account or a paid visit shuts it, as it drops the
 * card.
 */
export function limitWallDialogOpen(presentation: LimitWallPresentation, user: User,
	requested: boolean, paid: boolean, welcomeOn: boolean): boolean {
	return (presentation.kind === "coupon" || presentation.kind === "regular") && requested && !paid && !welcomeOn
		&& planOf(user) === "free";
}

/** Whether the session has answered at least once; a later Pending (a sign-in re-reading the account) keeps it. */
export function sessionSettledOnce(settledBefore: boolean, session: AuthenticatedSessionState): boolean {
	return settledBefore || session !== AuthenticatedSessionState.Pending;
}

/**
 * Subscribe through a sign-in: the checkout is decided at the moment it STARTS, from the presentation as
 * it is then, because a refusal can withdraw the coupon while the sign-in modal is up.
 */
export async function resumeLimitWallCheckout(ports: Omit<FreeCheckoutPorts, "start"> & {
	checkout: () => LimitWallCheckout | null;
	start: (checkout: LimitWallCheckout) => Promise<void>;
}): Promise<void> {
	await resumeFreeCheckout({...ports, start: async () => {
		const checkout = ports.checkout();
		if (checkout !== null) await ports.start(checkout);
	}});
}
